/**
 * How long a signed-in session survives — idle time, and closing the tab.
 *
 * The template's answer was "three days, silently". The access cookie lasts 15
 * minutes, but the refresh cookie is good for three days and `bootstrapSession()`
 * spends it automatically on every page load, so shutting the browser and coming
 * back ten hours later walked straight into the farm with no sign-in. Owner:
 * "should exit the game after 10 mins, and if I've closed the tab it should be
 * auto-logout — can't be that after 10 hours we still entered the game."
 *
 * Two rules, and they are different questions:
 *
 *  1. IDLE — ten minutes without touching the game signs you out. Idle, not
 *     absolute: an absolute cap would throw a player out mid-harvest, which is a
 *     bug rather than a policy. Any pointer, key, touch, wheel or tab-focus event
 *     is activity, so someone actually playing is never interrupted.
 *
 *  2. TAB CLOSED — the next visit must sign in again. `sessionStorage` is the
 *     right instrument and the only one that means exactly this: it is scoped to
 *     the tab, survives a reload (F5 must NOT sign you out), and is gone once the
 *     tab is closed. So a boot that finds valid cookies but no marker is a
 *     returning visitor, not a resumed session, and gets signed out.
 *
 * ONLY THE IDLE PATH revokes the token on the server. Going idle is the player
 * finishing; opening a second tab is not, and treating them the same is what made
 * this rule bite. `sessionStorage` is per TAB, so it is empty in a second tab, a
 * bookmark or a restored window — all of which looked like "the browser was
 * closed". Revoking there killed the refresh token the ORIGINAL tab was living on,
 * and that tab died as soon as its 15-minute access token lapsed. The tab rule now
 * declines to RESUME, which is all it ever needed to do.
 *
 * The idle countdown also pauses while the tab is hidden: it exists to end an
 * unattended session, and a game left open in a background tab is not that.
 *
 * This lives in game code for the same reason session-expiry.ts does — core owns
 * the auth client and the cookie lifetimes, and editing core conflicts on every
 * template merge. What the game can decide is when to spend the session and when
 * to throw it away, and that is what this does.
 */

import { logout } from '../core/auth/auth-api';
import { authStore } from '../core/state/auth-store';
import { toast } from './dom';

/** Ten minutes of no input ends the session. */
export const IDLE_LIMIT_MS = 10 * 60 * 1000;

/** Marks a tab that has an open session. Dies with the tab; survives a reload. */
const TAB_KEY = 'tb-tab-session';

/**
 * Events that count as "still here".
 *
 * `visibilitychange` is in the list because coming back to a backgrounded tab is
 * a deliberate return; without it the timer would fire the moment you looked at a
 * game you had been watching in another window.
 */
const ACTIVITY = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'visibilitychange'] as const;

let timer = 0;
let detach: (() => void) | null = null;
let signingOut = false;

/** Remember that THIS tab owns a session. Called on sign-in and on a live boot. */
export function markTabSession(): void {
  try { sessionStorage.setItem(TAB_KEY, '1'); } catch { /* private mode */ }
}

/**
 * Did this tab open the session that the cookies belong to?
 *
 * False after the tab (or the browser) was closed and reopened, which is the
 * case the owner reported. True across a reload, which must stay signed in.
 * Storage being unavailable answers TRUE: a browser that refuses sessionStorage
 * would otherwise sign the player out on every single load.
 */
export function tabOwnsSession(): boolean {
  try { return sessionStorage.getItem(TAB_KEY) === '1'; } catch { return true; }
}

function clearTabSession(): void {
  try { sessionStorage.removeItem(TAB_KEY); } catch { /* private mode */ }
}

/**
 * End the session and send the player to the sign-in screen.
 *
 * `reason` is shown so being signed out never looks like the game breaking —
 * the same lesson session-expiry.ts was written for.
 */
export async function endSession(reason: string): Promise<void> {
  if (signingOut) return;
  signingOut = true;
  stopIdleWatch();
  clearTabSession();
  // Clear the STORE FIRST, before the network call and before the toast delay.
  // Pollers (the HP bar re-reads /field/health every few seconds) keep firing
  // while this runs, and against a revoked token each one 401s into the console.
  // With no user in the store they stand down instead.
  authStore.set({ user: null, status: 'idle', error: null });
  toast(reason, 'error');
  await logout().catch(() => undefined);       // revokes the refresh token server-side
  // A beat so the toast is readable before the view changes — matching the
  // expired-session path, which players already recognise.
  setTimeout(() => { window.location.assign('/login'); }, 1200);
}

/**
 * End the session in THIS TAB only, leaving the credential alone.
 *
 * Used where the session is fine and merely does not belong to this tab. The
 * server-side revoke is reserved for `endSession`, where the player really has
 * finished — going idle is a decision, opening a second tab is not.
 */
export function signOutLocally(reason: string): void {
  if (signingOut) return;
  signingOut = true;
  stopIdleWatch();
  clearTabSession();
  authStore.set({ user: null, status: 'idle', error: null });
  toast(reason, 'error');
  signingOut = false;
}

/**
 * Sign out a session the cookies would otherwise resume in a NEW tab.
 *
 * Called from the router at boot, before the game mounts. Returns true when it
 * took over, so the caller stops rather than rendering a view it is about to
 * navigate away from.
 */
export async function enforceTabSession(): Promise<boolean> {
  if (!authStore.get().user || tabOwnsSession()) return false;
  signOutLocally('Sign in to continue');
  return false;
}

/** Start (or restart) the ten-minute idle countdown. */
export function startIdleWatch(): void {
  stopIdleWatch();
  signingOut = false;
  const bump = () => {
    window.clearTimeout(timer);
    // A HIDDEN tab does not count down. The timer is here to end an UNATTENDED
    // session, and a backgrounded game is not that — a farming game is left open
    // in a tab as a matter of course, and killing it there means coming back to a
    // login screen for having read something else for ten minutes. The countdown
    // restarts from zero when the tab is looked at again (visibilitychange bumps).
    if (document.hidden) return;
    timer = window.setTimeout(
      () => { void endSession('Signed out after 10 minutes of inactivity'); },
      IDLE_LIMIT_MS,
    );
  };
  for (const ev of ACTIVITY) window.addEventListener(ev, bump, { passive: true });
  document.addEventListener('visibilitychange', bump, { passive: true });
  detach = () => {
    for (const ev of ACTIVITY) window.removeEventListener(ev, bump);
    document.removeEventListener('visibilitychange', bump);
  };
  bump();
}

export function stopIdleWatch(): void {
  window.clearTimeout(timer);
  timer = 0;
  detach?.();
  detach = null;
}

/** Test seam: forget that a sign-out is in progress. */
export function __resetSessionPolicy(): void {
  signingOut = false;
  stopIdleWatch();
}
